import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useLike } from '../context/LikeContext'
import { Heart, Eye, EyeOff, X, Volume2, VolumeX, FileText } from 'lucide-react'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  display: flex;
  gap: 40px;
  justify-content: center;
  align-items: flex-start;
  
  @media (max-width: 1200px) {
    padding: 20px;
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px 15px;
    gap: 30px;
    align-items: center;
  }
  
  @media (max-width: 650px) {
    padding: 15px 10px;
  }
`

const LeftSection = styled.div`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 1200px) {
    flex: 0 0 280px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    max-width: none;
    align-items: center;
  }
`

const ProblemSetCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  @media (max-width: 1200px) {
    padding: 25px;
  }
  
  @media (max-width: 768px) {
    padding: 24px;
    width: 100%;
    min-width: 450px;
    max-width: none;
  }
  
  @media (max-width: 500px) {
    min-width: 400px;
    padding: 20px;
  }
  
  @media (max-width: 420px) {
    min-width: 350px;
    padding: 18px;
    border-radius: 15px;
  }
`

const ARObjectImage = styled.div`
  width: 300px;
  height: 400px;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin: 0 auto 20px;
  
  @media (max-width: 768px) {
    width: 120px;
    height: 160px;
  }
  
  @media (max-width: 480px) {
    width: 100px;
    height: 130px;
    border-radius: 8px;
  }
`

const ProblemSetTitle = styled.h2`
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px 0;
  line-height: 1.4;
`

const ProblemSetStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`

const HeartButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  margin: 8px 0;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  svg {
    transition: all 0.2s ease;
  }
  
  &.liked svg {
    fill: #ff4757;
    color: #ff4757;
  }
  
  span {
    font-weight: 600;
    color: ${props => props.theme.textSecondary};
  }
`

const LoginRequiredModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background-color: ${props => props.theme.cardBackground};
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  box-shadow: ${props => props.theme.shadowHover};
  max-width: 400px;
  width: 90%;
`

const ModalTitle = styled.h2`
  color: ${props => props.theme.textPrimary};
  margin-bottom: 20px;
  font-size: 24px;
`

const ModalMessage = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 30px;
  line-height: 1.5;
`

const ModalButton = styled.button`
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  margin: 0 10px;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
`

const ModalSecondaryButton = styled(ModalButton)`
  background-color: transparent;
  color: ${props => props.theme.textSecondary};
  border: 1px solid ${props => props.theme.border};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`

const EnlargedImageContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
`

const EnlargedImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: -40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    top: -30px;
    right: -30px;
    width: 30px;
    height: 30px;
  }
`

const PlayButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`

const PlayButton = styled.button`
  padding: 15px 25px;
  border-radius: 25px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }
  }
  
  &.secondary {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(240, 147, 251, 0.4);
    }
  }
`

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 800px;
  
  @media (max-width: 1200px) {
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    flex: none;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`

const AnswerToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.buttonSecondary};
  color: ${props => props.$active ? 'white' : props.theme.textPrimary};
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  width: 100%;
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.buttonSecondaryHover};
    transform: translateY(-1px);
  }
  
  svg {
    transition: all 0.3s ease;
  }
`

const ProblemCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 1200px) {
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 10px;
  }
`

const ProblemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`

const ARObjectIcon = styled.div`
  width: 60px;
  height: 60px;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  border: 2px solid ${props => props.theme.border};
`

const ProblemTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`

const ProblemContent = styled.div`
  margin-bottom: 20px;
`

const ProblemText = styled.p`
  color: ${props => props.theme.textPrimary};
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 15px 0;
`

const AttachedImage = styled.div`
  width: 200px;
  height: 150px;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.theme.border};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 180px;
    height: 135px;
  }
  
  @media (max-width: 480px) {
    width: 150px;
    height: 112px;
    border-radius: 6px;
  }
`

const ChoicesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`

const ChoiceButton = styled.button`
  flex: 0 0 calc(50% - 7.5px);
  padding: 15px 20px;
  border: 2px solid ${props => props.$isCorrect && props.$showAnswers ? '#4caf50' : props.theme.border};
  border-radius: 12px;
  background-color: ${props => props.$isCorrect && props.$showAnswers ? 'rgba(76, 175, 80, 0.1)' : props.theme.cardBackground};
  color: ${props => props.$isCorrect && props.$showAnswers ? '#4caf50' : props.theme.textPrimary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  font-weight: ${props => props.$isCorrect && props.$showAnswers ? '600' : 'normal'};
  
  &:hover {
    background-color: ${props => props.$isCorrect && props.$showAnswers ? 'rgba(76, 175, 80, 0.2)' : props.theme.hoverBackground};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    flex: 0 0 calc(50% - 5px);
    padding: 12px 15px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    flex: 0 0 calc(50% - 4px);
    padding: 10px 12px;
    font-size: 12px;
    border-radius: 8px;
  }
`

const CorrectAnswerLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: rgba(76, 175, 80, 0.1);
  color: #4caf50;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  flex-wrap: wrap;
`

const AttachmentsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
`

const AudioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const AudioButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  
  &:hover {
    color: ${props => props.theme.buttonPrimary};
  }
  
  &.playing {
    color: ${props => props.theme.buttonPrimary};
  }
`

const FormulaContainer = styled.div`
  padding: 12px 16px;
  background-color: rgba(33, 150, 243, 0.05);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 8px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .formula-icon {
    color: #2196f3;
    flex-shrink: 0;
  }
  
  .formula-text {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: ${props => props.theme.textPrimary};
    background-color: ${props => props.theme.cardBackground};
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.border};
    flex: 1;
    word-break: break-all;
  }
`

const ToggleIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.theme.hoverBackground || '#f8f9fa'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  font-size: 14px;
  color: ${props => props.theme.textSecondary || '#666'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background: ${props => props.theme.buttonPrimary || '#007bff'};
    color: white;
    transform: ${props => props.$isCollapsed ? 'rotate(-90deg) scale(1.05)' : 'rotate(0deg) scale(1.05)'};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  }
`

const ArObjectProblemsContainer = styled.div`
  transition: all 0.3s ease;
  overflow: hidden;
  max-height: ${props => props.$isCollapsed ? '0' : 'none'};
  opacity: ${props => props.$isCollapsed ? '0' : '1'};
  margin-bottom: ${props => props.$isCollapsed ? '0' : '20px'};
`

const ArObjectHeaderClickable = styled.div`
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
  padding: 16px 20px;
  background: ${props => props.theme.cardBackground};
  border-radius: 12px;
  margin: 10px 0;
  border: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`

const ModeModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModeModalContent = styled.div`
  background-color: ${props => props.theme.cardBackground};
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: ${props => props.theme.shadowHover};
  max-width: 450px;
  width: 90%;
`

const ModeModalTitle = styled.h2`
  color: ${props => props.theme.textPrimary};
  margin-bottom: 15px;
  font-size: 26px;
  font-weight: 700;
`

const ModeModalSubtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 30px;
  line-height: 1.5;
  font-size: 15px;
`

const ModeOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
`

const ModeOptionButton = styled.button`
  background: ${props => props.$variant === 'qr'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'};
  color: white;
  border: none;
  padding: 18px 30px;
  border-radius: 15px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$variant === 'qr'
      ? '0 8px 20px rgba(102, 126, 234, 0.4)'
      : '0 8px 20px rgba(240, 147, 251, 0.4)'};
  }

  &:active {
    transform: translateY(0);
  }
`

export default function ProblemDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { isLoggedIn } = useAuth()
  const { toggleLike, isLiked, initializeLikeStates } = useLike()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [showAnswers, setShowAnswers] = useState(false)
  const [enlargedImage, setEnlargedImage] = useState(null)
  const [currentProblemSet, setCurrentProblemSet] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [playingAudio, setPlayingAudio] = useState(null) // 현재 재생 중인 오디오 ID
  const [audioElements, setAudioElements] = useState({}) // 오디오 엘리먼트들 관리
  // AR 객체별 토글 상태 관리 (기본값은 모두 열림)
  const [arObjectToggled, setArObjectToggled] = useState({})
  // SOLO PLAY 모드 선택 모달
  const [showModeModal, setShowModeModal] = useState(false)

  // AR 객체별 섹션 토글 함수
  const toggleArObjectSection = (arObjectName) => {
    setArObjectToggled(prev => ({
      ...prev,
      [arObjectName]: !prev[arObjectName]
    }))
  }

  // API에서 퀴즈 데이터 가져오기
  useEffect(() => {
    const fetchQuizDetail = async () => {
      try {
        setLoading(true)
        setError(null)
        
        console.log('퀴즈 상세 정보 요청 - ID:', id)
        
        const response = await fetch(`/auth/get-quiz-detail/${id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        console.log('퀴즈 상세 응답 상태:', response.status)
        
        if (!response.ok) {
          throw new Error(`퀴즈 데이터를 가져올 수 없습니다. (${response.status})`)
        }
        
        const data = await response.json()
        console.log('퀴즈 상세 데이터:', data)
        
        if (data.success && data.quiz) {
          console.log('퀴즈 상세 데이터 로드 완료:', {
            title: data.quiz.set_title,
            problemCount: data.quiz.problems?.length || 0,
            arObjects: [...new Set(data.quiz.problems?.map(p => p.arObject || p.ar_object_name) || [])]
          })

          // AR 객체별 토글 상태 초기화 (모든 섹션 열림)
          const arObjectNames = [...new Set(data.quiz.problems?.map(p => p.arObject || p.ar_object_name || '알 수 없는 객체') || [])]
          const initialToggleState = {}
          arObjectNames.forEach(name => {
            initialToggleState[name] = false // false = 열림, true = 닫힘
          })

          // 토글 상태를 먼저 설정한 후 문제 세트 설정
          setArObjectToggled(initialToggleState)
          setCurrentProblemSet(data.quiz)
          setLikeCount(parseInt(data.quiz.set_like_count) || 0)

          // ★ 퀴즈 로드와 함께 좋아요 상태도 로드
          try {
            const likeResponse = await fetch('/auth/get-liked-quizzes', {
              method: 'GET',
              credentials: 'include'
            })

            const likeData = await likeResponse.json()

            if (likeData.success && likeData.likedQuizzes) {
              const formattedLikedSets = likeData.likedQuizzes.map(quiz => ({
                id: parseInt(quiz.set_id)  // set_id를 숫자로 변환
              }))

              // 전역 좋아요 상태 초기화
              initializeLikeStates(formattedLikedSets)
              console.log('❤️ [ProblemDetailPage] 좋아요 상태 로드 완료:', formattedLikedSets.length, '개')
            }
          } catch (likeError) {
            console.log('❤️ [ProblemDetailPage] 좋아요 상태 로드 스킵 (비로그인 또는 오류)')
          }
        } else {
          throw new Error(data.message || '퀴즈 데이터를 찾을 수 없습니다.')
        }
      } catch (error) {
        console.error('퀴즈 상세 정보 가져오기 오류:', error)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }
    
    if (id) {
      fetchQuizDetail()
    }
  }, [id])

  const handleHeartClick = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true)
      return
    }

    const result = await toggleLike(parseInt(id))

    if (result.success) {
      // 좋아요 수 업데이트 - 좋아요/취소에 따라 +1/-1
      setLikeCount(prev => result.isLiked ? prev + 1 : prev - 1)
    } else {
      alert(result.error)
    }
  }

  const handleLoginModalClose = () => {
    setShowLoginModal(false)
  }

  const handleLoginRedirect = () => {
    setShowLoginModal(false)
    navigate('/login?from=/problem/' + id)
  }

  const handleImageClick = (imageSrc) => {
    setEnlargedImage(imageSrc)
  }

  const handleCloseImageModal = () => {
    setEnlargedImage(null)
  }

  // 음성 파일 재생/정지 함수
  const handleAudioToggle = (audioUrl, problemId) => {
    const audioId = `${problemId}_audio`
    
    // 다른 오디오가 재생 중이면 정지
    if (playingAudio && playingAudio !== audioId) {
      const currentAudio = audioElements[playingAudio]
      if (currentAudio) {
        currentAudio.pause()
        currentAudio.currentTime = 0
      }
    }
    
    // 현재 오디오 엘리먼트 가져오기 또는 생성
    let audio = audioElements[audioId]
    if (!audio) {
      audio = new Audio(audioUrl)
      audio.addEventListener('ended', () => {
        setPlayingAudio(null)
      })
      audio.addEventListener('error', (e) => {
        console.error('오디오 재생 오류:', e)
        alert('오디오 파일을 재생할 수 없습니다.')
        setPlayingAudio(null)
      })
      setAudioElements(prev => ({ ...prev, [audioId]: audio }))
    }
    
    // 재생/정지 토글
    if (playingAudio === audioId) {
      audio.pause()
      audio.currentTime = 0
      setPlayingAudio(null)
    } else {
      audio.play().then(() => {
        setPlayingAudio(audioId)
      }).catch(error => {
        console.error('오디오 재생 실패:', error)
        alert('오디오 파일을 재생할 수 없습니다.')
      })
    }
  }

  // 컴포넌트 언마운트 시 모든 오디오 정지
  useEffect(() => {
    return () => {
      Object.values(audioElements).forEach(audio => {
        if (audio) {
          audio.pause()
          audio.currentTime = 0
        }
      })
    }
  }, [audioElements])

  if (loading) {
    return (
      <Container theme={theme}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          color: theme.textPrimary 
        }}>
          퀴즈 정보를 불러오는 중...
        </div>
      </Container>
    )
  }
  
  if (error || !currentProblemSet) {
    return (
      <Container theme={theme}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          color: theme.textPrimary,
          textAlign: 'center' 
        }}>
          {error || '문제 세트를 찾을 수 없습니다.'}
        </div>
      </Container>
    )
  }

  return (
    <Container theme={theme}>
      <LeftSection>
        <ProblemSetCard theme={theme}>
          <ARObjectImage $image={currentProblemSet.set_cover_img} />
          <ProblemSetTitle theme={theme}>{currentProblemSet.set_title}</ProblemSetTitle>
          <ProblemSetStats theme={theme}>
            <HeartButton
              theme={theme}
              className={isLiked(parseInt(id)) ? 'liked' : ''}
              onClick={handleHeartClick}
            >
              <Heart size={16} />
              <span>{likeCount}</span>
            </HeartButton>
            <div>👤 {currentProblemSet.set_author}</div>
            <div>{currentProblemSet.set_description || '퀴즈에 대한 설명이 없습니다.'}</div>
          </ProblemSetStats>
          <PlayButtons>
            <PlayButton className="primary" onClick={() => {
              // Ensure we pass a valid set identifier and problems for downstream pages
              const effectiveId = currentProblemSet?.set_id || currentProblemSet?.id || id
              navigate('/game-ready', {
                state: {
                  problemSet: {
                    id: effectiveId,
                    set_id: effectiveId, // include both keys for compatibility
                    title: currentProblemSet?.set_title || currentProblemSet?.title,
                    set_obj_count: currentProblemSet?.set_obj_count,
                    problems: currentProblemSet?.problems,
                    questions: currentProblemSet?.problems, // alt key used by some consumers
                    set_cover_img: currentProblemSet?.set_cover_img || currentProblemSet?.image || currentProblemSet?.cover_image,  // ★ 커버 이미지 추가
                    image: currentProblemSet?.set_cover_img || currentProblemSet?.image || currentProblemSet?.cover_image  // ★ 호환성 유지
                  },
                  objectCount:
                    currentProblemSet?.set_obj_count || currentProblemSet?.problems?.length || 12
                }
              })
            }}>
              ▶ PLAY
            </PlayButton>
            <PlayButton className="secondary" onClick={() => setShowModeModal(true)}>
              SOLO PLAY
            </PlayButton>
            <AnswerToggleButton 
              theme={theme} 
              $active={showAnswers}
              onClick={() => setShowAnswers(!showAnswers)}
            >
              {showAnswers ? <EyeOff size={16} /> : <Eye size={16} />}
              {showAnswers ? '정답 숨기기' : '모든 정답 보기'}
            </AnswerToggleButton>
          </PlayButtons>
        </ProblemSetCard>
      </LeftSection>

      <RightSection>
        
        {currentProblemSet.problems && (() => {
          // AR 객체별로 문제들을 그룹화
          const problemsByARObject = currentProblemSet.problems.reduce((acc, problem) => {
            const arObjectKey = problem.arObject || problem.ar_object_name || '알 수 없는 객체';
            
            if (!acc[arObjectKey]) {
              acc[arObjectKey] = {
                arObjectName: arObjectKey,
                arObjectImage: problem.ar_object_image || problem.arObjectImage || currentProblemSet.set_cover_img,
                problems: []
              };
            }
            acc[arObjectKey].problems.push(problem);
            return acc;
          }, {});

          return Object.entries(problemsByARObject).map(([arObjectKey, group]) => (
            <div key={arObjectKey}>
              {/* AR 객체별 섹션 헤더 */}
              <div 
                style={{
                  padding: '20px 24px',
                  background: `linear-gradient(135deg, ${theme.cardBackground} 0%, ${theme.hoverBackground || '#f8f9fa'} 100%)`,
                  borderRadius: '16px',
                  margin: '10px 0',
                  border: `2px solid ${theme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  userSelect: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => toggleArObjectSection(arObjectKey)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${theme.hoverBackground || '#f0f4f8'} 0%, ${theme.buttonPrimary || '#007bff'}15 100%)`
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${theme.cardBackground} 0%, ${theme.hoverBackground || '#f8f9fa'} 100%)`
                  e.currentTarget.style.transform = 'translateY(0px)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
                }}
              >
                <img 
                  src={group.arObjectImage} 
                  alt={group.arObjectName}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    objectFit: 'cover',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    border: `2px solid ${theme.border}`,
                    transition: 'transform 0.3s ease'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '18px', 
                    fontWeight: '700',
                    color: theme.textPrimary,
                    letterSpacing: '-0.02em'
                  }}>
                    {group.arObjectName}
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '13px', 
                    color: theme.textSecondary,
                    fontWeight: '500',
                    opacity: 0.8
                  }}>
                    {group.problems.length}개 문제
                  </p>
                </div>
                <ToggleIcon $isCollapsed={arObjectToggled[arObjectKey] || false}>
                  {(arObjectToggled[arObjectKey] || false) ? '▶' : '▼'}
                </ToggleIcon>
              </div>

              {/* 해당 AR 객체의 문제들 */}
              <ArObjectProblemsContainer $isCollapsed={arObjectToggled[arObjectKey] || false}>
              {group.problems.map((problem, index) => {
                // 선택지를 배열로 파싱 (API에서 문자열로 올 수 있음)
                const choices = typeof problem.choices === 'string' 
                  ? JSON.parse(problem.choices) 
                  : problem.choices || []
                
                // 정답 배열로 파싱 (DB에는 0-based로 저장되어 있음: 0, 1, 2, 3)
                // 프론트엔드도 0-based 사용: 0, 1, 2, 3
                const correctAnswer = (() => {
                  try {
                    let parsed = [];
                    if (typeof problem.correct_answer === 'string') {
                      parsed = JSON.parse(problem.correct_answer);
                    } else if (Array.isArray(problem.correct_answer)) {
                      parsed = problem.correct_answer;
                    } else if (typeof problem.correct_answer === 'number') {
                      parsed = [problem.correct_answer];
                    } else if (problem.correctAnswer) {
                      // 백업 필드명
                      parsed = Array.isArray(problem.correctAnswer) ? problem.correctAnswer : [problem.correctAnswer];
                    }

                    // 0-based 그대로 사용 (변환 불필요)
                    return parsed;
                  } catch (e) {
                    console.warn('정답 파싱 실패:', problem.correct_answer, e);
                    return [];
                  }
                })()
                
                return (
                  <ProblemCard key={problem.question_id || problem.id || index} theme={theme}>
                    <ProblemHeader>
                      <ARObjectIcon $image={problem.ar_object_image || problem.arObjectImage || currentProblemSet.set_cover_img} theme={theme} />
                      <ProblemTitle theme={theme}>{problem.question_text || problem.question}</ProblemTitle>
                    </ProblemHeader>
                    
                    <ProblemContent>
                      {/* 첨부 파일들 표시 */}
                      <AttachmentsContainer>
                        {(() => {
                          // 첨부 이미지 파싱 (JSON 문자열 또는 배열)
                          const attachedImages = (() => {
                            const attachedImg = problem.attachments?.attached_img || problem.attached_img;
                            if (attachedImg) {
                              try {
                                const parsed = typeof attachedImg === 'string' 
                                  ? JSON.parse(attachedImg) 
                                  : attachedImg;
                                return Array.isArray(parsed) ? parsed : [parsed];
                              } catch (e) {
                                console.warn('attached_img 파싱 실패:', attachedImg);
                                return [];
                              }
                            }
                            // 기존 필드명도 지원
                            if (problem.attached_image || problem.attachedImage) {
                              return [{ url: problem.attached_image || problem.attachedImage }];
                            }
                            return [];
                          })();

                          // 첨부 음성 파싱
                          const attachedAudios = (() => {
                            const attachedVoice = problem.attachments?.attached_voice || problem.attached_voice;
                            if (attachedVoice) {
                              try {
                                const parsed = typeof attachedVoice === 'string' 
                                  ? JSON.parse(attachedVoice) 
                                  : attachedVoice;
                                return Array.isArray(parsed) ? parsed : [parsed];
                              } catch (e) {
                                console.warn('attached_voice 파싱 실패:', attachedVoice);
                                return [];
                              }
                            }
                            return [];
                          })();

                          return (
                            <>
                              {/* 첨부 이미지들 표시 */}
                              {attachedImages.map((img, imgIndex) => (
                                <AttachedImage 
                                  key={`img-${imgIndex}`}
                                  $image={img.url} 
                                  theme={theme} 
                                  onClick={() => handleImageClick(img.url)}
                                />
                              ))}
                              
                              {/* 첨부 음성들 표시 */}
                              {attachedAudios.map((audio, audioIndex) => (
                                <AudioContainer key={`audio-${audioIndex}`} theme={theme}>
                                  <AudioButton
                                    className={playingAudio === `${problem.set_id}_audio_${audioIndex}` ? 'playing' : ''}
                                    onClick={() => handleAudioToggle(audio.url, `${problem.set_id}_${audioIndex}`)}
                                  >
                                    {playingAudio === `${problem.set_id}_audio_${audioIndex}` ? (
                                      <VolumeX size={16} />
                                    ) : (
                                      <Volume2 size={16} />
                                    )}
                                    {playingAudio === `${problem.set_id}_audio_${audioIndex}` ? '재생 중...' : `음성 재생 ${audio.name ? `(${audio.name})` : ''}`}
                                  </AudioButton>
                                </AudioContainer>
                              ))}
                            </>
                          );
                        })()}
                      </AttachmentsContainer>
                      
                      {/* 첨부 수식 */}
                      {(() => {
                        const attachedFormula = problem.attachments?.attached_formula || problem.attached_formula;
                        if (attachedFormula) {
                          try {
                            const formulas = typeof attachedFormula === 'string' 
                              ? JSON.parse(attachedFormula) 
                              : attachedFormula;
                            const formulaArray = Array.isArray(formulas) ? formulas : [formulas];
                            
                            return formulaArray.map((formula, formulaIndex) => (
                              <FormulaContainer key={`formula-${formulaIndex}`} theme={theme}>
                                <FileText size={16} className="formula-icon" />
                                <div className="formula-text">
                                  {formula.content || formula.equation || formula}
                                </div>
                              </FormulaContainer>
                            ));
                          } catch (e) {
                            console.warn('attached_formula 파싱 실패:', attachedFormula);
                            return (
                              <FormulaContainer theme={theme}>
                                <FileText size={16} className="formula-icon" />
                                <div className="formula-text">
                                  {attachedFormula}
                                </div>
                              </FormulaContainer>
                            );
                          }
                        }
                        return null;
                      })()}
                      
                      {showAnswers && correctAnswer.length > 0 && (
                        <CorrectAnswerLabel>
                          ✓ 정답: {correctAnswer.map(index => choices[index]).filter(Boolean).join(', ')}
                        </CorrectAnswerLabel>
                      )}
                      
                      <ChoicesContainer>
                        {choices.map((choice, choiceIndex) => (
                          <ChoiceButton
                            key={choiceIndex}
                            theme={theme}
                            $isCorrect={correctAnswer.includes(choiceIndex)}
                            $showAnswers={showAnswers}
                          >
                            {choice}
                          </ChoiceButton>
                        ))}
                      </ChoicesContainer>
                      
                      {(problem.explanation || problem.problem_explanation) && (
                        <ProblemText theme={theme}>
                          💡 {problem.explanation || problem.problem_explanation}
                        </ProblemText>
                      )}
                    </ProblemContent>
                  </ProblemCard>
                );
              })}
              </ArObjectProblemsContainer>
            </div>
          ));
        })()}
      </RightSection>
      
      {/* 로그인 필요 모달 */}
      {showLoginModal && (
        <LoginRequiredModal onClick={handleLoginModalClose}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalTitle theme={theme}>로그인이 필요한 서비스입니다</ModalTitle>
            <ModalMessage theme={theme}>
              좋아요 기능을 사용하려면 로그인이 필요합니다.<br/>
              로그인하시겠습니까?
            </ModalMessage>
            <div>
              <ModalButton theme={theme} onClick={handleLoginRedirect}>
                로그인
              </ModalButton>
              <ModalSecondaryButton theme={theme} onClick={handleLoginModalClose}>
                취소
              </ModalSecondaryButton>
            </div>
          </ModalContent>
        </LoginRequiredModal>
      )}
      
      {/* 이미지 확대 모달 */}
      {enlargedImage && (
        <ImageModal onClick={handleCloseImageModal}>
          <EnlargedImageContainer onClick={e => e.stopPropagation()}>
            <EnlargedImage src={enlargedImage} alt="확대된 이미지" />
            <CloseButton onClick={handleCloseImageModal}>
              <X size={20} />
            </CloseButton>
          </EnlargedImageContainer>
        </ImageModal>
      )}

      {/* SOLO PLAY 모드 선택 모달 */}
      {showModeModal && (
        <ModeModal onClick={() => setShowModeModal(false)}>
          <ModeModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModeModalTitle theme={theme}>SOLO PLAY 모드 선택</ModeModalTitle>
            <ModeModalSubtitle theme={theme}>
              플레이 방식을 선택해주세요
            </ModeModalSubtitle>
            <ModeOptionsContainer>
              <ModeOptionButton
                $variant="qr"
                onClick={() => {
                  setShowModeModal(false)
                  navigate('/ar-game-solo', {
                    state: {
                      problemSet: {
                        id: currentProblemSet?.set_id || id,
                        title: currentProblemSet?.set_title,
                        set_obj_count: currentProblemSet?.set_obj_count,
                        problems: currentProblemSet?.problems
                      },
                      objectCount: currentProblemSet?.set_obj_count || currentProblemSet?.problems?.length || 12,
                      mode: 'qr-scan'
                    }
                  })
                }}
              >
                🎯 QR 스캔 모드
                <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.9 }}>
                  QR 인쇄 후 스캔하여 플레이
                </div>
              </ModeOptionButton>
              <ModeOptionButton
                $variant="normal"
                onClick={() => {
                  setShowModeModal(false)
                  navigate('/ar-game-solo', {
                    state: {
                      problemSet: {
                        id: currentProblemSet?.set_id || id,
                        title: currentProblemSet?.set_title,
                        set_obj_count: currentProblemSet?.set_obj_count,
                        problems: currentProblemSet?.problems
                      },
                      objectCount: currentProblemSet?.set_obj_count || currentProblemSet?.problems?.length || 12,
                      mode: 'normal'
                    }
                  })
                }}
              >
                ⚡ 일반 모드
                <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.9 }}>
                  QR 스캔 없이 바로 플레이
                </div>
              </ModeOptionButton>
            </ModeOptionsContainer>
            <ModalSecondaryButton theme={theme} onClick={() => setShowModeModal(false)}>
              취소
            </ModalSecondaryButton>
          </ModeModalContent>
        </ModeModal>
      )}
    </Container>
  )
}
